import React, { useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  IconButton,
  Tooltip,
  LinearProgress,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Devices as DevicesIcon,
  SmartToy as SmartToyIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Computer as ComputerIcon,
  Api as ApiIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  TrendingUp as TrendingUpIcon,
  Error as ErrorIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { 
  Client, 
  AgentClient, 
  UIClient, 
  APIClient, 
  ClientType,
  ClientAnalytics,
} from '../hooks/useApplicationObservatory';

interface ClientsPanelProps {
  clients: (Client | AgentClient | UIClient | APIClient)[];
  analytics?: Record<string, ClientAnalytics>;
  compact?: boolean;
  onClientClick?: (client: Client) => void;
}

export const ClientsPanel: React.FC<ClientsPanelProps> = ({
  clients,
  analytics = {},
  compact = false,
  onClientClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ClientType | 'all'>('all');
  const [expandedClient, setExpandedClient] = useState<string | null>(null);

  const getClientIcon = (type: ClientType) => {
    switch (type) {
      case 'agent':
      case 'mcp':
        return <SmartToyIcon />;
      case 'ui':
        return <DevicesIcon />;
      case 'api':
      case 'webhook':
        return <ApiIcon />;
      default:
        return <DevicesIcon />;
    }
  };

  const getClientColor = (type: ClientType): 'primary' | 'secondary' | 'success' | 'info' | 'warning' => {
    switch (type) {
      case 'agent':
        return 'primary';
      case 'mcp':
        return 'secondary';
      case 'ui':
        return 'success';
      case 'api':
        return 'info';
      case 'webhook':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'mobile':
        return <PhoneAndroidIcon fontSize="small" />;
      case 'desktop':
      case 'web':
        return <ComputerIcon fontSize="small" />;
      default:
        return <DevicesIcon fontSize="small" />;
    }
  };

  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesSearch = client.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || client.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [clients, searchQuery, typeFilter]);

  const formatDate = (date?: Date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  const isAgentClient = (client: Client): client is AgentClient => 
    client.type === 'agent' || client.type === 'mcp';
  
  const isUIClient = (client: Client): client is UIClient => 
    client.type === 'ui';

  const renderClientDetails = (client: Client) => {
    if (isAgentClient(client)) {
      return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {client.model && (
            <Chip label={client.model} size="small" variant="outlined" />
          )}
          <Chip 
            icon={<TrendingUpIcon />}
            label={`${client.conversationCount} conversations`} 
            size="small" 
            variant="outlined" 
          />
          {client.capabilities?.map(cap => (
            <Chip key={cap} label={cap} size="small" variant="outlined" />
          ))}
        </Box>
      );
    }
    
    if (isUIClient(client)) {
      return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Chip
            icon={getPlatformIcon(client.platform)}
            label={client.platform}
            size="small"
            variant="outlined"
          />
          {client.version && (
            <Chip label={`v${client.version}`} size="small" variant="outlined" />
          )}
          <Chip label={`${client.userCount} users`} size="small" variant="outlined" />
          <Chip label={`${client.sessionCount} sessions`} size="small" variant="outlined" />
        </Box>
      );
    }

    return null;
  };

  const renderAnalyticsPreview = (clientId: string) => {
    const clientAnalytics = analytics[clientId];
    if (!clientAnalytics) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Analytics (Last {clientAnalytics.period})
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {clientAnalytics.totalRequests}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Requests
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h6" 
                sx={{ fontWeight: 'bold', color: clientAnalytics.errorRate > 5 ? 'error.main' : 'success.main' }}
              >
                {clientAnalytics.errorRate}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Error Rate
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {clientAnalytics.avgResponseTime}ms
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Response
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {clientAnalytics.p95ResponseTime}ms
              </Typography>
              <Typography variant="caption" color="text.secondary">
                P95
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {clientAnalytics.errors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
              Recent Errors ({clientAnalytics.errors.length})
            </Typography>
            {clientAnalytics.errors.slice(0, 3).map((error, idx) => (
              <Chip
                key={idx}
                icon={<ErrorIcon />}
                label={`${error.type}: ${error.count}`}
                size="small"
                color="error"
                variant="outlined"
                sx={{ mr: 0.5, mt: 0.5 }}
              />
            ))}
          </Box>
        )}

        {clientAnalytics.topFunctions.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Top Functions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
              {clientAnalytics.topFunctions.slice(0, 3).map((func, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.5,
                    bgcolor: 'background.default',
                    borderRadius: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', flex: 1 }}>
                    {func.name}()
                  </Typography>
                  <Chip label={`${func.callCount}`} size="small" />
                  <Typography variant="caption" color="text.secondary">
                    {func.avgDuration}ms
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DevicesIcon />
            <Typography variant="h6">Clients</Typography>
            <Chip label={clients.length} size="small" color="primary" />
          </Box>
        }
        subheader="Agent, UI, and API clients consuming application interfaces"
      />
      <CardContent>
        {/* Filters */}
        {!compact && (
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <ToggleButtonGroup
                  value={typeFilter}
                  exclusive
                  onChange={(e, newValue) => newValue && setTypeFilter(newValue)}
                  size="small"
                >
                  <ToggleButton value="all">
                    All ({clients.length})
                  </ToggleButton>
                  <ToggleButton value="agent">
                    <SmartToyIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    Agent ({clients.filter(c => c.type === 'agent').length})
                  </ToggleButton>
                  <ToggleButton value="mcp">
                    <SmartToyIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    MCP ({clients.filter(c => c.type === 'mcp').length})
                  </ToggleButton>
                  <ToggleButton value="ui">
                    <DevicesIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    UI ({clients.filter(c => c.type === 'ui').length})
                  </ToggleButton>
                  <ToggleButton value="api">
                    <ApiIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    API ({clients.filter(c => c.type === 'api').length})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Summary Chips */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={<SmartToyIcon />}
            label={`${clients.filter(c => c.type === 'agent' || c.type === 'mcp').length} AI Clients`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<DevicesIcon />}
            label={`${clients.filter(c => c.type === 'ui').length} UI Clients`}
            size="small"
            color="success"
            variant="outlined"
          />
          <Chip
            icon={<ApiIcon />}
            label={`${clients.filter(c => c.type === 'api' || c.type === 'webhook').length} API Clients`}
            size="small"
            color="info"
            variant="outlined"
          />
        </Box>

        {/* Clients List */}
        {filteredClients.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No clients found
          </Typography>
        ) : (
          <List sx={{ maxHeight: compact ? 300 : 600, overflow: 'auto' }}>
            {filteredClients.map((client) => (
              <React.Fragment key={client.id}>
                <ListItem
                  sx={{
                    mb: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => {
                    if (compact) {
                      onClientClick?.(client);
                    } else {
                      setExpandedClient(expandedClient === client.id ? null : client.id);
                    }
                  }}
                >
                  <ListItemIcon>
                    {getClientIcon(client.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {client.name}
                        </Typography>
                        <Chip
                          label={client.type.toUpperCase()}
                          size="small"
                          color={getClientColor(client.type)}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                          → {client.applicationKey}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {client.description}
                        </Typography>
                        {compact && renderClientDetails(client)}
                        {!compact && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Last active: {formatDate(
                                isAgentClient(client) ? client.lastConversation :
                                isUIClient(client) ? client.lastSession :
                                undefined
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                  {!compact && (
                    <IconButton size="small">
                      {expandedClient === client.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                </ListItem>

                {!compact && (
                  <Collapse in={expandedClient === client.id} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 7, pr: 2, pb: 2 }}>
                      <Divider sx={{ my: 1 }} />
                      {renderClientDetails(client)}
                      {renderAnalyticsPreview(client.id)}
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Chip
                          label="View Traces"
                          size="small"
                          clickable
                          onClick={() => console.log('View traces for', client.id)}
                        />
                        <Chip
                          label="View Analytics"
                          size="small"
                          clickable
                          onClick={() => console.log('View analytics for', client.id)}
                        />
                        {isAgentClient(client) && (
                          <Chip
                            label="Open Agent"
                            size="small"
                            icon={<OpenInNewIcon />}
                            clickable
                            onClick={() => console.log('Open agent', client.id)}
                          />
                        )}
                      </Box>
                    </Box>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default ClientsPanel;

